import type { User } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/user_pb";
import type { AdminUserType } from "components/Admin/types";
import type IUser from "models/User.model";
import {
  GlobalUserType,
  type IdeConfig,
  type UserOnboardingStatus,
  VerificationStatus,
} from "models/User.model";

type JsonRecord = Record<string, unknown>;

function getMetadata(user: User): JsonRecord {
  const metadata = user.metadata?.toJson();
  return metadata && typeof metadata === "object" && !Array.isArray(metadata)
    ? metadata
    : {};
}

function getRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : {};
}

function getString(metadata: JsonRecord, ...keys: string[]): string {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string") return value;
  }
  return "";
}

function getBoolean(metadata: JsonRecord, ...keys: string[]): boolean {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "boolean") return value;
  }
  return false;
}

function mapOnboardingData(metadata: JsonRecord): UserOnboardingStatus {
  const onboarding = getRecord(
    metadata.onboardingData ?? metadata.OnboardingData,
  );
  const value = (camelCaseKey: string, legacyKey: string) =>
    onboarding[camelCaseKey] ??
    onboarding[legacyKey] ??
    metadata[camelCaseKey] ??
    metadata[legacyKey];

  return {
    editor: value("editor", "Editor") as string | undefined,
    SSH: Boolean(value("SSH", "ssh")),
    usedCLI: Boolean(value("usedCLI", "UsedCLI")),
    attemptedToConnectCloud: Boolean(
      value("attemptedToConnectCloud", "AttemptedToConnectCloud"),
    ),
    whyBrev: (value("whyBrev", "WhyBrev") as string | undefined) ?? "",
    templateChosen:
      (value("templateChosen", "TemplateChosen") as string | undefined) ?? "",
    caltechOffer:
      (value("caltechOffer", "CaltechOffer") as string | undefined) ?? "",
    inviteAccepted:
      (value("inviteAccepted", "InviteAccepted") as string | undefined) ?? "",
    instacart: (value("instacart", "Instacart") as string | undefined) ?? "",
    closedOnboardings: Boolean(value("closedOnboardings", "ClosedOnboardings")),
    clickedOnboarding: Array.isArray(
      value("clickedOnboarding", "ClickedOnboarding"),
    )
      ? (value("clickedOnboarding", "ClickedOnboarding") as string[])
      : [],
    isTermsAccepted: Boolean(value("isTermsAccepted", "IsTermsAccepted")),
    showedNvidiaWelcome: Boolean(
      value("showedNvidiaWelcome", "ShowedNvidiaWelcome"),
    ),
  };
}

export function mapCurrentUser(user: User): IUser {
  const metadata = getMetadata(user);
  const externalIdentities = user.externalIdentities
    .map((identity) => identity.externalId)
    .filter(Boolean);
  const apiKeys = getRecord(metadata.apiKeys ?? metadata.ApiKeys);
  const ideConfig = getRecord(metadata.ideConfig ?? metadata.IDEConfig);

  return {
    id: user.userId,
    username: user.username,
    name: user.displayName,
    email: user.defaultEmail || user.emails[0]?.emailAddress || "",
    externalAuthId: externalIdentities[0] ?? "",
    externalIdentities,
    organizationAccesses: user.organizationAccesses,
    workspacePassword: getString(
      metadata,
      "workspacePassword",
      "WorkspacePassword",
    ),
    publicKey: getString(metadata, "publicKey", "PublicKey") || undefined,
    baseWorkspaceRepo:
      getString(metadata, "baseWorkspaceRepo", "BaseWorkspaceRepo") ||
      undefined,
    userSetupExecPath:
      getString(metadata, "userSetupExecPath", "UserSetupExecPath") ||
      undefined,
    verificationStatus:
      (getString(
        metadata,
        "verificationStatus",
        "VerificationStatus",
      ) as VerificationStatus) || VerificationStatus.Unverified,
    globalUserType:
      (getString(
        metadata,
        "globalUserType",
        "GlobalUserType",
      ) as GlobalUserType) || GlobalUserType.Standard,
    onboardingData: mapOnboardingData(metadata),
    apiKeys: {
      ngc: getString(apiKeys, "ngc", "NGC"),
      huggingFace: getString(apiKeys, "huggingFace", "HuggingFace"),
    },
    ideConfig: ideConfig as IdeConfig,
    isBlocked: getBoolean(metadata, "isBlocked", "IsBlocked"),
    platformRole: user.platformRole,
  };
}

export function mapAdminUser(user: User): AdminUserType {
  const metadata = getMetadata(user);
  const externalIdentities = user.externalIdentities.map((identity) => ({
    identity_id: identity.identityId,
    provider: identity.provider,
    external_id: identity.externalId,
  }));

  return {
    id: user.userId,
    created_at: user.createTime?.toDate().toISOString() ?? "",
    updated_at: user.updateTime?.toDate().toISOString() ?? "",
    deleted_at: null,
    Username: user.username,
    Name: user.displayName,
    Email: user.defaultEmail,
    ExternalAuthID: externalIdentities[0]?.external_id ?? "",
    ExternalPaymentID: "",
    BaseWorkspaceRepo: "",
    UserDotBrevPath: "",
    UserSetupExecPath: "",
    IDEConfig: { defaultWorkingDir: "", vscode: { extensions: null } },
    WorkspacePassword: "",
    VerificationStatus: "",
    BillingStatus: "",
    GlobalUserType:
      typeof metadata.globalUserType === "string"
        ? metadata.globalUserType
        : "",
    OnboardingData: {
      isTermsAccepted:
        typeof metadata.isTermsAccepted === "boolean"
          ? metadata.isTermsAccepted
          : false,
      showedNvidiaWelcome:
        typeof metadata.showedNvidiaWelcome === "boolean"
          ? metadata.showedNvidiaWelcome
          : false,
    },
    ImageRegistryUri: "",
    IsBlocked:
      typeof metadata.isBlocked === "boolean" ? metadata.isBlocked : false,
    ApiKeys: null,
    ExternalIdentities: externalIdentities,
    PlatformRole: user.platformRole,
  };
}
